import { CANTON_NAMES, CANTON_OPTIONS, LabelValue, Select, TextField } from 'shared-components';
import { FormsKeys, Namespace } from '@i18n/translations';
import { type FunctionComponent, type ReactNode, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type PharmacyFormFieldsProps } from './PharmacyFormFields.types';

const COUNTRY_OPTIONS = [{ value: 'CH', label: 'Schweiz' }];

const ReadOnlyField = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-1">
    <LabelValue label={label}>{children}</LabelValue>
  </div>
);

const PharmacyFormFields: FunctionComponent<PharmacyFormFieldsProps> = ({
  control,
  isSubmitted,
  disabled,
  readOnly,
}) => {
  const { t, i18n } = useTranslation([Namespace.Forms]);

  const nameField = useController({ name: 'name', control });
  const streetField = useController({ name: 'street', control });
  const zipField = useController({ name: 'zip', control });
  const cityField = useController({ name: 'city', control });
  const cantonField = useController({ name: 'canton', control });
  const countryField = useController({ name: 'country', control });
  const phonePrivateField = useController({ name: 'phonePrivate', control });
  const phoneBusinessField = useController({ name: 'phoneBusiness', control });
  const phoneMobileField = useController({ name: 'phoneMobile', control });
  const faxField = useController({ name: 'fax', control });

  const cantonOptions = useMemo(() => {
    const localized = CANTON_NAMES[i18n.language];
    if (!localized) return CANTON_OPTIONS;
    return CANTON_OPTIONS.map(({ value }) => ({
      value,
      label: localized[value] ?? value,
    }));
  }, [i18n.language]);

  const displayValue = (value?: string | null) => value || '-';

  const optionLabel = (options: Array<{ value: string; label: string }>, value?: string | null) =>
    displayValue(options.find((option) => option.value === value)?.label ?? value);

  return (
    <div className="flex flex-col gap-2">
      {readOnly ? (
        <>
          <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Name)}>{displayValue(nameField.field.value)}</ReadOnlyField>
          <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Street)}>
            {displayValue(streetField.field.value)}
          </ReadOnlyField>
        </>
      ) : (
        <>
          <TextField
            name="name"
            label={t(FormsKeys.Defaults_Labels_Name)}
            value={nameField.field.value ?? ''}
            onValueChange={nameField.field.onChange}
            invalid={Boolean(nameField.fieldState.invalid && isSubmitted)}
            errorMessage={nameField.fieldState.error?.message}
            disabled={disabled}
            required
            controlledMode
            hideOptionalLabel
          />
          <TextField
            name="street"
            label={t(FormsKeys.Defaults_Labels_Street)}
            value={streetField.field.value ?? ''}
            onValueChange={streetField.field.onChange}
            invalid={Boolean(streetField.fieldState.invalid && isSubmitted)}
            errorMessage={streetField.fieldState.error?.message}
            disabled={disabled}
            required
            controlledMode
            hideOptionalLabel
          />
        </>
      )}

      <div className="grid gap-2 md:grid-cols-2">
        {readOnly ? (
          <>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Postalcode)}>
              {displayValue(zipField.field.value)}
            </ReadOnlyField>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_City)}>
              {displayValue(cityField.field.value)}
            </ReadOnlyField>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Canton)}>
              {optionLabel(cantonOptions, cantonField.field.value)}
            </ReadOnlyField>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Country)}>
              {optionLabel(COUNTRY_OPTIONS, countryField.field.value)}
            </ReadOnlyField>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Tel_Private)}>
              {displayValue(phonePrivateField.field.value)}
            </ReadOnlyField>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Tel_Mobile)}>
              {displayValue(phoneMobileField.field.value)}
            </ReadOnlyField>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Tel_Business)}>
              {displayValue(phoneBusinessField.field.value)}
            </ReadOnlyField>
            <ReadOnlyField label={t(FormsKeys.Defaults_Labels_Fax)}>{displayValue(faxField.field.value)}</ReadOnlyField>
          </>
        ) : (
          <>
            <TextField
              name="zip"
              label={t(FormsKeys.Defaults_Labels_Postalcode)}
              value={zipField.field.value ?? ''}
              onValueChange={zipField.field.onChange}
              invalid={Boolean(zipField.fieldState.invalid && isSubmitted)}
              errorMessage={zipField.fieldState.error?.message}
              inputMode="numeric"
              maxLength={5}
              disabled={disabled}
              required
              controlledMode
              hideOptionalLabel
            />
            <TextField
              name="city"
              label={t(FormsKeys.Defaults_Labels_City)}
              value={cityField.field.value ?? ''}
              onValueChange={cityField.field.onChange}
              invalid={Boolean(cityField.fieldState.invalid && isSubmitted)}
              errorMessage={cityField.fieldState.error?.message}
              disabled={disabled}
              required
              controlledMode
              hideOptionalLabel
            />
            <Select
              name="canton"
              label={t(FormsKeys.Defaults_Labels_Canton)}
              value={cantonField.field.value ?? ''}
              options={cantonOptions}
              onValueChange={cantonField.field.onChange}
              invalid={Boolean(cantonField.fieldState.invalid && isSubmitted)}
              errorMessage={cantonField.fieldState.error?.message}
              disabled={disabled}
              required
              hideOptionalLabel
            />
            <Select
              name="country"
              label={t(FormsKeys.Defaults_Labels_Country)}
              value={countryField.field.value ?? ''}
              options={COUNTRY_OPTIONS}
              onValueChange={countryField.field.onChange}
              invalid={Boolean(countryField.fieldState.invalid && isSubmitted)}
              errorMessage={countryField.fieldState.error?.message}
              disabled={disabled}
              required
              hideOptionalLabel
            />
            <TextField
              name="phonePrivate"
              label={t(FormsKeys.Defaults_Labels_Tel_Private)}
              type="tel"
              value={phonePrivateField.field.value ?? ''}
              onValueChange={phonePrivateField.field.onChange}
              invalid={Boolean(phonePrivateField.fieldState.invalid && isSubmitted)}
              errorMessage={phonePrivateField.fieldState.error?.message}
              disabled={disabled}
              controlledMode
            />
            <TextField
              name="phoneMobile"
              label={t(FormsKeys.Defaults_Labels_Tel_Mobile)}
              type="tel"
              value={phoneMobileField.field.value ?? ''}
              onValueChange={phoneMobileField.field.onChange}
              invalid={Boolean(phoneMobileField.fieldState.invalid && isSubmitted)}
              errorMessage={phoneMobileField.fieldState.error?.message}
              disabled={disabled}
              controlledMode
            />
            <TextField
              name="phoneBusiness"
              label={t(FormsKeys.Defaults_Labels_Tel_Business)}
              type="tel"
              value={phoneBusinessField.field.value ?? ''}
              onValueChange={phoneBusinessField.field.onChange}
              invalid={Boolean(phoneBusinessField.fieldState.invalid && isSubmitted)}
              errorMessage={phoneBusinessField.fieldState.error?.message}
              disabled={disabled}
              controlledMode
            />
            <TextField
              name="fax"
              label={t(FormsKeys.Defaults_Labels_Fax)}
              type="tel"
              value={faxField.field.value ?? ''}
              onValueChange={faxField.field.onChange}
              invalid={Boolean(faxField.fieldState.invalid && isSubmitted)}
              errorMessage={faxField.fieldState.error?.message}
              disabled={disabled}
              controlledMode
            />
          </>
        )}
      </div>
    </div>
  );
};

PharmacyFormFields.displayName = 'PharmacyFormFields';

export { PharmacyFormFields };
